export type SseItem = {
	event: string;
	id: string;
	data: string;
	retry?: number | undefined;
};

// https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation
export async function* processSseStream(reader: ReadableStreamDefaultReader<string>): AsyncIterable<SseItem> {
	let buffer: string = "";

	let pendingEvent: SseItem = {
		event: "",
		id: "",
		data: ""
	};

	let isDoneReading: boolean = false;

	for (; ;) {
		const lineFeedIndex: number = buffer.indexOf("\n");
		const carageReturnIndex: number = buffer.indexOf("\r");

		let endOfLine: number | undefined;
		let numberOfLineEndingCharacters: number = 0;

		if (lineFeedIndex !== -1) {
			if (carageReturnIndex !== -1 && carageReturnIndex + 1 === lineFeedIndex) {
				// \r\n
				endOfLine = carageReturnIndex;
				numberOfLineEndingCharacters = 2;
			} else {
				// \n
				endOfLine = lineFeedIndex;
				numberOfLineEndingCharacters = 1;
			}
		} else if (carageReturnIndex !== -1) {
			// Deal with the edge case that the CR was the last char in the buffer and we don't know
			// if the character after it is going to be an LF
			if (buffer.length > (carageReturnIndex + 1) || isDoneReading) {
				// \r
				endOfLine = carageReturnIndex;
				numberOfLineEndingCharacters = 1;
			}
		}

		if (typeof endOfLine !== "undefined") {
			const line: string = buffer.slice(0, endOfLine);
			buffer = buffer.slice(endOfLine + numberOfLineEndingCharacters);

			if (line === "") {
				const event: SseItem = pendingEvent;

				if (event.data !== "") {
					if (event.data.endsWith("\n")) {
						event.data = event.data
							.substring(0, event.data.length - 1);
					}

					yield event;
				}

				pendingEvent = {
					event: "",
					id: "",
					data: ""
				};
			} else if (line.startsWith(":")) {
				// comment (ignore)
			} else if (line.includes(":")) {
				const colonIndex: number = line.indexOf(":");
				const field: string = line.slice(0, colonIndex);
				let value: string = line.slice(colonIndex + 1);

				if (value.startsWith(" ")) {
					value = value.slice(1);
				}

				processField(
					field,
					value,
					pendingEvent
				);
			} else {
				const field: string = line;
				const value: string = "";

				processField(
					field,
					value,
					pendingEvent
				);
			}
		} else {
			if (isDoneReading) {
				if (buffer.length > 0) {
					throw new Error(`Abrupt end to the SSE stream, remaining in buffer: '${buffer}'`);
				}
				break;
			}

			const chunkResult: ReadableStreamReadResult<string> = await reader.read();
			isDoneReading = chunkResult.done;
			if (chunkResult.value) {
				buffer += chunkResult.value;
			}
		}
	}
}

function processField(
	field: string,
	value: string,
	pendingEvent: SseItem
): void {
	switch (field) {
		case "event":
			pendingEvent.event = value;
			break;
		case "data":
			pendingEvent.data += value + "\n";
			break;
		case "id":
			if (value !== String.fromCharCode(0)) {
				pendingEvent.id = value;
			}
			break;
		case "retry":
			if (/^-?\d+$/.test(value)) {
				pendingEvent.retry = Number(value);
			}
			break;
		default:
			// Ignore
			break;
	}
}
